"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

import { Button, Input, Label, Modal, Textarea } from "@/components/ui-system";

type DialogTone = "default" | "danger";

type ConfirmOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: DialogTone;
};

type AlertOptions = {
  title: string;
  description?: string;
  acknowledgeLabel?: string;
  tone?: DialogTone;
};

type InputOptions = {
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  multiline?: boolean;
};

type CopyOptions = {
  title: string;
  description?: string;
  label?: string;
  value: string;
  closeLabel?: string;
  copyLabel?: string;
};

type DialogRequest =
  | { kind: "confirm"; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: "alert"; options: AlertOptions; resolve: () => void }
  | { kind: "input"; options: InputOptions; resolve: (value: string | null) => void }
  | { kind: "copy"; options: CopyOptions; resolve: () => void };

type AppDialogContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  alert: (options: AlertOptions) => Promise<void>;
  input: (options: InputOptions) => Promise<string | null>;
  copy: (options: CopyOptions) => Promise<void>;
};

const AppDialogContext = createContext<AppDialogContextValue | null>(null);

function resolveVariant(tone: DialogTone | undefined): "primary" | "danger" {
  return tone === "danger" ? "danger" : "primary";
}

export function AppDialogProvider({ children }: { children: ReactNode }) {
  const [activeDialog, setActiveDialog] = useState<DialogRequest | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [copied, setCopied] = useState(false);
  const queueRef = useRef<DialogRequest[]>([]);

  const showNext = useCallback(() => {
    const next = queueRef.current.shift() ?? null;
    setActiveDialog(next);
    if (next?.kind === "input") {
      setInputValue(next.options.initialValue ?? "");
    } else {
      setInputValue("");
    }
    setCopied(false);
  }, []);

  const enqueue = useCallback((request: DialogRequest) => {
    if (activeDialog === null) {
      setActiveDialog(request);
      if (request.kind === "input") {
        setInputValue(request.options.initialValue ?? "");
      } else {
        setInputValue("");
      }
      setCopied(false);
      return;
    }
    queueRef.current.push(request);
  }, [activeDialog]);

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setInputValue("");
    setCopied(false);
    window.setTimeout(showNext, 0);
  }, [showNext]);

  const contextValue = useMemo<AppDialogContextValue>(
    () => ({
      confirm: (options) =>
        new Promise<boolean>((resolve) => {
          enqueue({ kind: "confirm", options, resolve });
        }),
      alert: (options) =>
        new Promise<void>((resolve) => {
          enqueue({ kind: "alert", options, resolve });
        }),
      input: (options) =>
        new Promise<string | null>((resolve) => {
          enqueue({ kind: "input", options, resolve });
        }),
      copy: (options) =>
        new Promise<void>((resolve) => {
          enqueue({ kind: "copy", options, resolve });
        }),
    }),
    [enqueue],
  );

  const handleConfirm = useCallback(() => {
    if (activeDialog?.kind !== "confirm") {
      return;
    }
    activeDialog.resolve(true);
    closeDialog();
  }, [activeDialog, closeDialog]);

  const handleCancel = useCallback(() => {
    if (activeDialog?.kind === "confirm") {
      activeDialog.resolve(false);
    } else if (activeDialog?.kind === "input") {
      activeDialog.resolve(null);
    } else if (activeDialog?.kind === "alert") {
      activeDialog.resolve();
    } else if (activeDialog?.kind === "copy") {
      activeDialog.resolve();
    }
    closeDialog();
  }, [activeDialog, closeDialog]);

  const handleSubmitInput = useCallback(() => {
    if (activeDialog?.kind !== "input") {
      return;
    }
    activeDialog.resolve(inputValue);
    closeDialog();
  }, [activeDialog, closeDialog, inputValue]);

  const handleCopy = useCallback(async () => {
    if (activeDialog?.kind !== "copy") {
      return;
    }
    try {
      await navigator.clipboard.writeText(activeDialog.options.value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }, [activeDialog]);

  return (
    <AppDialogContext.Provider value={contextValue}>
      {children}
      {activeDialog ? (
        <Modal
          open
          onClose={handleCancel}
          title={activeDialog.options.title}
          description={activeDialog.options.description}
          size={activeDialog.kind === "copy" ? "lg" : "md"}
          footer={
            activeDialog.kind === "confirm" ? (
              <div className="flex justify-end gap-ds-2">
                <Button type="button" variant="secondary" size="sm" onClick={handleCancel}>
                  {activeDialog.options.cancelLabel ?? "Cancel"}
                </Button>
                <Button type="button" variant={resolveVariant(activeDialog.options.tone)} size="sm" onClick={handleConfirm}>
                  {activeDialog.options.confirmLabel ?? "Confirm"}
                </Button>
              </div>
            ) : activeDialog.kind === "alert" ? (
              <div className="flex justify-end gap-ds-2">
                <Button type="button" variant={resolveVariant(activeDialog.options.tone)} size="sm" onClick={handleCancel}>
                  {activeDialog.options.acknowledgeLabel ?? "OK"}
                </Button>
              </div>
            ) : activeDialog.kind === "input" ? (
              <div className="flex justify-end gap-ds-2">
                <Button type="button" variant="secondary" size="sm" onClick={handleCancel}>
                  {activeDialog.options.cancelLabel ?? "Cancel"}
                </Button>
                <Button type="button" variant="primary" size="sm" onClick={handleSubmitInput}>
                  {activeDialog.options.confirmLabel ?? "Continue"}
                </Button>
              </div>
            ) : (
              <div className="flex justify-end gap-ds-2">
                <Button type="button" variant="secondary" size="sm" onClick={handleCancel}>
                  {activeDialog.options.closeLabel ?? "Close"}
                </Button>
                <Button type="button" variant="primary" size="sm" onClick={() => void handleCopy()}>
                  {copied ? "Copied" : activeDialog.options.copyLabel ?? "Copy"}
                </Button>
              </div>
            )
          }
        >
          {activeDialog.kind === "confirm" || activeDialog.kind === "alert" ? null : activeDialog.kind === "input" ? (
            <label className="flex flex-col gap-ds-2">
              {activeDialog.options.label ? <Label>{activeDialog.options.label}</Label> : null}
              {activeDialog.options.multiline ? (
                <Textarea
                  className="min-h-[8rem]"
                  autoFocus
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder={activeDialog.options.placeholder}
                />
              ) : (
                <Input
                  autoFocus
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder={activeDialog.options.placeholder}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleSubmitInput();
                    }
                  }}
                />
              )}
            </label>
          ) : (
            <div className="space-y-ds-3">
              {activeDialog.options.label ? <Label>{activeDialog.options.label}</Label> : null}
              <Textarea
                className="min-h-[10rem] font-mono text-caption"
                readOnly
                value={activeDialog.options.value}
                onFocus={(event) => event.currentTarget.select()}
              />
              <p className="form-helper">
                {copied
                  ? "Copied to clipboard."
                  : "If clipboard access is unavailable, you can still select and copy the value manually."}
              </p>
            </div>
          )}
        </Modal>
      ) : null}
    </AppDialogContext.Provider>
  );
}

export function useAppDialogs(): AppDialogContextValue {
  const context = useContext(AppDialogContext);
  if (!context) {
    throw new Error("useAppDialogs must be used within AppDialogProvider");
  }
  return context;
}
